// Routing / intent debug — minimal view for Pipeline Progress drawer.
import React, { useState } from 'react';
const ROUTING_KEYS = [
    'domain',
    'action',
    'params',
    'route_node',
    'classifier',
    'llm_used',
    'llm_role',
    'model_id',
    'matched_rule',
    'normalizations',
    'user_message',
    'router_context',
    'prompt_sent',
    'raw_llm_route',
    'needs_clarification',
    'clarification_reason',
    'resource_intent',
    'pending_action',
];
const CATEGORY_BLURBS = {
    'Intent classification': 'What the request is about, what to do, and which IDs were extracted. This is the structured intent, not the final wording of the answer.',
    'Dispatch': 'Which specialist node actually ran after the intent was classified.',
    'Classification process': 'Whether the route came from a hard rule, from the router LLM plus rules, or from a safe fallback.',
    'Model usage': 'Whether a language model ran at this stage, which Bedrock model, and for which job (classify vs write).',
    'Deterministic guard': 'Fixed rules that bypass or correct the router so obvious or unsafe cases are not left to free-form LLM routing.',
    'Classification input': 'The exact sentence and the compact session context sent to the router. Secrets and huge payloads are excluded.',
    'Classification output': 'What the router LLM returned before post-rules. Compare it with the final domain/action to see corrections.',
    Safety: 'If the request is ambiguous, nothing scientific is launched until the user clarifies.',
    'Resource operation': 'Payload for list/check/delete/explain on existing MexaScreen resources.',
    Confirmation: 'An action waiting for an explicit click (docking launch, delete). Not yet executed.',
    'Response provenance': 'How the visible answer was produced: LLM, template, API formatting, or a mix.',
    'Response composition': 'Which layer chose the bubble text. It is not always the raw LLM string.',
    Grounding: 'Trusted facts given to the writer (session counts, target, IDs). Distinct from knowledge-catalog snippets.',
    'Response policy': 'Post-processing on the text (validate output, drop broken lists, keep answers short).',
    'Knowledge sources': 'Small versioned catalogue in the repo (catalog.json), not a vector database. Entries are selected by keywords for this turn. Later this can become RAG.',
    'Prompt engineering': 'Where the system prompt lives in the code.',
    'UniProt resolution': 'How a PDB was mapped to a UniProt accession.',
    'Scientific provenance': 'Public pages for the exact structure and protein entry.',
    'UniProt normalization': 'Which field names were kept after mapping the UniProt JSON.',
    'LangGraph storage': 'The full normalized target fiche kept in chatbot state for UI and later questions.',
    'LLM grounding': 'The compact subset of that fiche copied into a scientific question prompt. Not a second store.',
    Persistence: 'Where the fiche lives (checkpoint) and what is deliberately not stored (raw UniProt JSON, MexaScreen DB).',
    Retrieval: 'Live RCSB/UniProt call failed; a mock fiche may have been used.',
};
const FIELD_DOCS = {
    domain: {
        category: 'Intent classification',
        summary: 'Topic bucket. "What is Mexa" → general_conversation (talk), not receptor/peptide (job).',
        values: [
            'project', 'target', 'pdb', 'receptor', 'binding_site', 'screening',
            'peptide', 'docking', 'resource_ops', 'navigation',
            'general_conversation', 'clarification',
        ],
    },
    action: {
        category: 'Intent classification',
        summary: 'Verb inside that bucket. discuss = answer; launch/prepare = job; navigate = change step.',
        values: [
            'discuss', 'configure', 'prepare', 'launch', 'check', 'details', 'list',
            'select', 'stop', 'delete', 'relaunch', 'modify', 'navigate', 'continue',
            'set_main', 'query_main', 'check_all', 'explain', 'summary', 'count', 'clarify',
        ],
    },
    params: {
        category: 'Intent classification',
        summary: 'Concrete identifiers explicitly provided or uniquely recoverable from context.',
        fields: [
            'pdb_id', 'sequence', 'ui_index', 'backend_id', 'resource_type',
            'resource_types', 'target_step', 'binding_site', 'focus_property',
        ],
        details: [
            'Parameters do not decide the capability: domain selects the capability and action selects the operation.',
            'Null fields mean that the identifier was not present or could not be recovered unambiguously.',
            'The specialized node may perform additional deterministic resolution against the live session.',
        ],
    },
    route_node: {
        category: 'Dispatch',
        summary: 'The specialized LangGraph node derived from the final domain.',
        values: [
            'project_scoping', 'target_biology', 'pdb_selection', 'receptor_preparation',
            'binding_site', 'screening', 'peptide_design', 'docking', 'resource_ops',
            'navigation', 'general_conversation', 'clarification',
        ],
    },
    classifier: {
        category: 'Classification process',
        summary: 'How the final route was obtained.',
        values: ['deterministic_rule', 'llm_structured_plus_rules', 'fallback_clarification'],
    },
    llm_used: {
        category: 'Model usage',
        summary: 'Whether a language model wrote the visible answer. Router-model usage is shown separately in Routing / Intent.',
        values: ['yes', 'no'],
    },
    llm_role: {
        category: 'Model usage',
        summary: 'The logical purpose assigned to the model, independent of the model vendor.',
        values: ['router — intent classification only', 'main — answer formulation'],
    },
    model_id: {
        category: 'Model usage',
        summary: 'The model that wrote the visible answer. "none" means the answer came from trusted data plus deterministic code or a fixed product template.',
    },
    matched_rule: {
        category: 'Deterministic guard',
        summary: 'A high-confidence rule that classified the request without semantic LLM routing.',
    },
    normalizations: {
        category: 'Deterministic guard',
        summary: 'Safety or consistency rules applied after the structured LLM classification.',
        values: [
            'conceptual_question_guard', 'peptide_sequence_guard',
            'navigation_normalizer', 'docking_affinity_context_rule',
            'clarification_safety_route',
        ],
    },
    user_message: {
        category: 'Classification input',
        summary: 'The exact user message classified on this turn.',
    },
    router_context: {
        category: 'Classification input',
        summary: 'The sanitized state variables supplied to the intent router.',
        fields: [
            'active_step', 'main_pdb', 'main_receptor_id', 'selected_pdb',
            'receptor_id', 'peptide_id', 'docking_id', 'screening_id', 'binding_site',
            'has_project_brief', 'has_target_fiche', 'pending_action', 'pending_jobs',
            'last_result_context', 'has_screening_access', 'recent_turns',
        ],
        details: [
            'This context is intentionally compact: tokens, credentials and large backend payloads are excluded.',
            'Recent turns help resolve references such as “it”, while selected IDs anchor the route to session state.',
        ],
    },
    raw_llm_route: {
        category: 'Classification output',
        summary: 'The structured route returned by the router model before deterministic normalization.',
        fields: ['domain', 'action', 'params', 'needs_clarification', 'clarification_reason'],
    },
    needs_clarification: {
        category: 'Safety',
        summary: 'When true, no scientific side effect is allowed until the request is clarified.',
        values: ['yes', 'no'],
    },
    clarification_reason: {
        category: 'Safety',
        summary: 'The reason the request cannot be executed safely or unambiguously.',
    },
    resource_intent: {
        category: 'Resource operation',
        summary: 'The normalized operation payload consumed by the resource operations node.',
    },
    pending_action: {
        category: 'Confirmation',
        summary: 'An action waiting for explicit user approval before execution.',
    },
    technique: {
        category: 'Response provenance',
        summary: 'How this answer was built. llm_enriched = Nova wrote it, with session facts and catalogue text in the prompt.',
    },
    rules: {
        category: 'Response policy',
        summary: 'Post-model policy: validated_llm_output rejects Bedrock filter dumps; progressive_disclosure_75_words caps a first answer unless the user asked for detail.',
    },
    data_used: {
        category: 'Grounding',
        summary: 'Sanitized session facts JSON in the prompt (counts, main target, step). Distinguishes a grounded answer from model-only knowledge.',
        details: [
            'These values come from the current LangGraph state or the refreshed MexaScreen session snapshot.',
            'For "What is Mexa" this object is context only — product definition comes from knowledge entries.',
        ],
    },
    prompt_source: {
        category: 'Prompt engineering',
        summary: 'Code symbol for the writer system prompt. Router prompts live in graph/router_prompt.py instead.',
    },
    prompt_sent: {
        category: 'Prompt engineering',
        summary: 'Exact messages assembled and sent to the model for this turn (system + conversation). Click to read each block.',
        fields: ['summary', 'calls', 'purpose', 'model_id', 'messages', 'role', 'content', 'chars'],
        details: [
            'This is the assembled prompt that left the node — not a paraphrase of data_used.',
            'Tokens and JWTs are redacted. Very long blocks may be truncated with a marker.',
            'If this field is missing, no LLM call produced the visible answer (template / rule / API formatting).',
            'Multiple calls (e.g. project-scoping extraction + reply) appear as Call 1, Call 2.',
            'On Routing / Intent, this is the router-classifier prompt; on LLM Intelligence, it is the answer-writer prompt.',
        ],
    },
    source_node: {
        category: 'Response provenance',
        summary: 'LangGraph node that produced the turn. general_conversation = Q&A, not a job launch.',
    },
    bubble_source: {
        category: 'Response composition',
        summary: 'node_ai_message = the bubble is that node’s text. Other values mean a template or notice replaced it.',
        values: [
            'node_ai_message', 'navigation_ai_message', 'validation_template',
            'chat_notice_summary', 'notice_cards_only', 'job_pending_template',
            'continue_template', 'pending_confirmation_ai_message',
        ],
    },
    data_sources: {
        category: 'Grounding',
        summary: 'External systems or persisted stores consulted for this answer.',
    },
    knowledge_entries: {
        category: 'Knowledge sources',
        summary: 'Which internal product-knowledge blocks from catalog.json were injected into the answer prompt.',
        details: [
            'This is not UniProt and not MexaScreen live data. It is internal product text.',
            'assistant.operating_principles is usually always-on (safety / source order).',
            '"What is Mexa" typically also selects product.meddenovo_mexa and product.mexascreen (keyword match on mexa / mexascreen).',
            'Find the file at knowledge catalog file, then the object whose "id" equals the entry name.',
        ],
    },
    knowledge_location: {
        category: 'Knowledge sources',
        summary: 'Repository file containing the internal product-knowledge blocks: langgraph-agent/mexa_orchestrator/knowledge/catalog.json',
        details: [
            'Each entry has an id, optional always=true, and a content block.',
            'Selection is keyword/route based in knowledge/service.py — not embeddings yet.',
        ],
    },
    knowledge_catalog_version: {
        category: 'Knowledge sources',
        summary: 'Version field from catalog.json, so you can say which text pack was used.',
    },
    pdb_id: {
        category: 'UniProt resolution',
        summary: 'PDB structure used to resolve the corresponding UniProt accession.',
    },
    uniprot_id: {
        category: 'UniProt resolution',
        summary: 'Primary UniProt accession selected for the target.',
    },
    source: {
        category: 'UniProt resolution',
        summary: 'Resolution method used to connect the PDB structure to UniProt.',
        values: ['rcsb', 'uniprot-search', 'explicit-uniprot', 'demo'],
    },
    links: {
        category: 'Scientific provenance',
        summary: 'Direct links to the exact RCSB structure and UniProt entry.',
    },
    retrieved_fields: {
        category: 'UniProt normalization',
        summary: 'Names of the UniProt fields retained in the normalized target record.',
    },
    normalized_target_fiche: {
        category: 'LangGraph storage',
        summary: 'Complete normalized target record persisted in MexaState.target_fiche.',
        fields: ['target', 'uniprot_id', 'mechanism', 'sequence', 'details'],
        details: [
            'Root fields form the concise user-facing fiche.',
            'details contains protein name, gene, organism, full function, catalytic activities, key residues, cofactors, full canonical sequence, sequence length and provenance.',
            'The raw UniProt JSON is not retained after normalization.',
        ],
    },
    llm_context_used_for_questions: {
        category: 'LLM grounding',
        summary: 'Compact subset of target biology facts supplied to scientific answers.',
        details: [
            'This is derived from target_fiche when a scientific question is answered.',
            'The full canonical sequence is deliberately omitted to keep prompts small; sequence_length and selected biological facts remain available.',
            'No new UniProt request is made for each question: the persisted normalized fiche is reused.',
        ],
    },
    storage: {
        category: 'Persistence',
        summary: 'Where normalized UniProt data is stored and what is deliberately not retained.',
        fields: [
            'runtime', 'persistent', 'mexa_backend', 'raw_uniprot_json_stored',
            'normalized_record_stored', 'full_sequence',
        ],
        details: [
            'Runtime location: MexaState.target_fiche.',
            'Local persistence: LangGraph checkpoint in mexa_orchestrator/data/checkpoints.sqlite.',
            'Checkpoint identity: actor_id plus project/session_id.',
            'Deployment persistence depends on the configured checkpointer.',
            'The MexaScreen backend stores receptor PDB data, but not this normalized UniProt fiche.',
        ],
    },
    error: {
        category: 'Retrieval',
        summary: 'UniProt or RCSB retrieval error when live normalization failed.',
    },
};
const DEBUG_LABELS = {
    classifier: 'classification path',
    llm_used: 'LLM used',
    llm_role: 'LLM purpose',
    model_id: 'runtime model',
    matched_rule: 'direct rule',
    normalizations: 'post-LLM rules',
    user_message: 'classified message',
    router_context: 'classification inputs',
    raw_llm_route: 'raw LLM output',
    domain: 'final domain',
    action: 'final action',
    route_node: 'dispatched node',
    params: 'extracted parameters',
    resource_intent: 'resource operation',
    pending_action: 'pending confirmation',
    source_node: 'response node',
    bubble_source: 'visible text source',
    technique: 'technique',
    data_sources: 'live data sources',
    data_used: 'session facts in prompt',
    rules: 'applied rules',
    knowledge_entries: 'knowledge sources',
    knowledge_location: 'knowledge catalog file',
    knowledge_catalog_version: 'knowledge version',
    prompt_source: 'prompt source',
    prompt_sent: 'prompt sent',
    pdb_id: 'PDB ID',
    pdb_status: 'PDB status',
    pdb_source: 'PDB source',
    uniprot_id: 'UniProt accession',
    source: 'resolution source',
    retrieved_fields: 'normalized fields',
    normalized_target_fiche: 'stored target record',
    llm_context_used_for_questions: 'facts supplied to LLM',
    storage: 'storage details',
    error: 'retrieval error',
};
function isEmptyValue(value) {
    if (value == null)
        return true;
    if (value === false)
        return false;
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === 'object')
        return Object.keys(value).length === 0;
    return false;
}
function formatInline(value) {
    if (value == null)
        return '—';
    if (typeof value === 'boolean')
        return value ? 'yes' : 'no';
    if (typeof value === 'string' || typeof value === 'number')
        return String(value);
    try {
        return JSON.stringify(value);
    }
    catch (_a) {
        return String(value);
    }
}
function formatBlock(value) {
    if (value == null)
        return '—';
    if (typeof value === 'string')
        return value;
    try {
        return JSON.stringify(value, null, 2);
    }
    catch (_a) {
        return String(value);
    }
}
function formatCompact(value) {
    if (Array.isArray(value)) {
        if (!value.length)
            return '[]';
        const primitive = value.every((item) => ['string', 'number', 'boolean'].includes(typeof item));
        return primitive
            ? value.slice(0, 3).map(String).join(', ') + (value.length > 3 ? ` +${value.length - 3}` : '')
            : `${value.length} item${value.length === 1 ? '' : 's'} — click to inspect`;
    }
    if (value && typeof value === 'object') {
        const entries = Object.entries(value);
        const populated = entries.filter(([, item]) => !isEmptyValue(item));
        if (!populated.length)
            return '{ no populated fields }';
        const keys = populated.slice(0, 4).map(([key]) => key);
        return `{ ${keys.join(', ')}${populated.length > 4 ? ', …' : ''} }`;
    }
    return formatInline(value);
}
function compactForField(field, value) {
    if (field === 'prompt_sent' && value && typeof value === 'object' && !Array.isArray(value)) {
        const summary = value.summary;
        if (typeof summary === 'string' && summary.trim())
            return summary;
    }
    return formatCompact(value);
}
function isNested(value) {
    if (value == null)
        return false;
    if (typeof value === 'object') {
        if (Array.isArray(value))
            return value.length > 0;
        return Object.keys(value).length > 0;
    }
    return false;
}
/** Flat routing view from state_snapshot.routing_debug or legacy flat keys. */
function routingView(snapshot) {
    var _a;
    if (!snapshot)
        return {};
    const dedicated = snapshot.routing_debug;
    if (dedicated && typeof dedicated === 'object') {
        const view = dedicated;
        const classification = view.classification && typeof view.classification === 'object'
            ? view.classification
            : {};
        return Object.assign(Object.assign({}, classification), view);
    }
    const route = ((_a = snapshot.route) !== null && _a !== void 0 ? _a : {});
    const params = route.params;
    return {
        domain: route.domain,
        action: route.action,
        route_node: snapshot.route_node,
        needs_clarification: route.needs_clarification,
        clarification_reason: route.clarification_reason,
        active_step: snapshot.active_step,
        current_step: snapshot.current_step,
        params: params && typeof params === 'object' ? params : {},
        resource_intent: snapshot.resource_intent,
        pending_action: snapshot.pending_action,
    };
}
function RoutingRow({ label, displayLabel, value, onSelect }) {
    var _a;
    const empty = isEmptyValue(value);
    const nested = isNested(value);
    const doc = FIELD_DOCS[label];
    return (React.createElement("button", { type: "button", className: [
            'lg-routing-row',
            empty ? 'lg-routing-row-empty' : '',
        ].filter(Boolean).join(' '), title: doc === null || doc === void 0 ? void 0 : doc.summary, onClick: () => onSelect === null || onSelect === void 0 ? void 0 : onSelect(label) },
        React.createElement("span", { className: "lg-routing-key" }, (_a = displayLabel !== null && displayLabel !== void 0 ? displayLabel : DEBUG_LABELS[label]) !== null && _a !== void 0 ? _a : label),
        nested ? (React.createElement("span", { className: "lg-routing-val lg-routing-val-compact" }, compactForField(label, value))) : (React.createElement("span", { className: "lg-routing-val" }, formatInline(value)))));
}
function LinkRows({ value, onSelect, }) {
    if (!value || typeof value !== 'object')
        return null;
    return (React.createElement("div", { className: "lg-routing-row lg-routing-row-links", title: FIELD_DOCS.links.summary, role: "button", tabIndex: 0, onClick: onSelect, onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ')
                onSelect();
        } },
        React.createElement("span", { className: "lg-routing-key" }, "source links"),
        React.createElement("div", { className: "lg-debug-links" }, Object.entries(value).map(([label, href]) => (React.createElement("a", { key: label, href: String(href), target: "_blank", rel: "noreferrer", onClick: (event) => event.stopPropagation() }, label))))));
}
function PromptSentView({ value }) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return React.createElement("pre", { className: "lg-debug-field-current" }, formatBlock(value));
    }
    const record = value;
    const calls = Array.isArray(record.calls) ? record.calls : [];
    if (!calls.length) {
        return React.createElement("pre", { className: "lg-debug-field-current" }, formatBlock(value));
    }
    return (React.createElement("div", { className: "lg-debug-prompt" },
        typeof record.summary === 'string' && record.summary ? (React.createElement("p", { className: "lg-debug-prompt-summary" }, record.summary)) : null,
        calls.map((call, callIdx) => {
            const messages = Array.isArray(call.messages)
                ? call.messages
                : [];
            const purpose = String(call.purpose || `call ${callIdx + 1}`);
            const modelId = call.model_id ? String(call.model_id) : null;
            const note = call.note ? String(call.note) : null;
            return (React.createElement("section", { key: `call-${callIdx}`, className: "lg-debug-prompt-call" },
                React.createElement("header", { className: "lg-debug-prompt-call-header" },
                    React.createElement("strong", null,
                        "Call ",
                        callIdx + 1,
                        purpose ? ` · ${purpose}` : ''),
                    modelId ? React.createElement("span", null, modelId) : null),
                note ? React.createElement("p", { className: "lg-debug-prompt-note" }, note) : null,
                messages.map((message, msgIdx) => {
                    var _a;
                    const role = String(message.role || 'message');
                    const chars = message.chars != null ? String(message.chars) : null;
                    const truncated = Boolean(message.truncated);
                    const omitted = message.omitted_middle_messages;
                    return (React.createElement("article", { key: `msg-${callIdx}-${msgIdx}`, className: "lg-debug-prompt-message" },
                        React.createElement("span", { className: "lg-debug-field-caption" },
                            role,
                            chars ? ` · ${chars} chars` : '',
                            truncated ? ' · truncated' : '',
                            omitted ? ` · ${omitted} middle messages omitted` : ''),
                        React.createElement("pre", { className: "lg-debug-field-current" }, String((_a = message.content) !== null && _a !== void 0 ? _a : ''))));
                })));
        })));
}
function FieldDetail({ field, value, onClose, }) {
    var _a, _b, _c, _d, _e;
    const doc = FIELD_DOCS[field];
    if (!doc)
        return null;
    return (React.createElement("div", { className: "lg-debug-field-overlay", role: "presentation", onClick: onClose, onKeyDown: (event) => {
            if (event.key === 'Escape')
                onClose();
        } },
        React.createElement("section", { className: "lg-debug-field-detail", role: "dialog", "aria-modal": "true", "aria-label": `${(_a = DEBUG_LABELS[field]) !== null && _a !== void 0 ? _a : field} explanation`, onClick: (event) => event.stopPropagation() },
            React.createElement("header", { className: "lg-debug-field-detail-header" },
                React.createElement("div", null,
                    React.createElement("span", { className: "lg-debug-field-category" }, doc.category),
                    React.createElement("strong", null, (_b = DEBUG_LABELS[field]) !== null && _b !== void 0 ? _b : field),
                    CATEGORY_BLURBS[doc.category] ? (React.createElement("p", { className: "lg-debug-field-category-blurb" }, CATEGORY_BLURBS[doc.category])) : null),
                React.createElement("button", { type: "button", onClick: onClose, "aria-label": "Close explanation" }, "\u00D7")),
            React.createElement("div", { className: "lg-debug-field-detail-body" },
                React.createElement("p", null, doc.summary),
                React.createElement("span", { className: "lg-debug-field-caption" }, "Current value"),
                field === 'prompt_sent' ? (React.createElement(PromptSentView, { value: value })) : (React.createElement("pre", { className: "lg-debug-field-current" }, formatBlock(value))),
                ((_c = doc.details) === null || _c === void 0 ? void 0 : _c.length) ? (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "lg-debug-field-caption" }, "How to read it"),
                    React.createElement("ul", { className: "lg-debug-field-notes" }, doc.details.map((detail) => React.createElement("li", { key: detail }, detail))))) : null,
                ((_d = doc.values) === null || _d === void 0 ? void 0 : _d.length) ? (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "lg-debug-field-caption" }, "Possible values"),
                    React.createElement("div", { className: "lg-debug-field-options" }, doc.values.map((value) => React.createElement("code", { key: value }, value))))) : null,
                ((_e = doc.fields) === null || _e === void 0 ? void 0 : _e.length) ? (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "lg-debug-field-caption" }, "Possible fields"),
                    React.createElement("div", { className: "lg-debug-field-options" }, doc.fields.map((value) => React.createElement("code", { key: value }, value))))) : null))));
}
function DebugFieldList({ keys, row, labelOverrides, }) {
    const [selectedField, setSelectedField] = useState(null);
    return (React.createElement("div", { className: "lg-debug-fields-layout" },
        React.createElement("div", { className: "lg-routing-rows" }, keys.map((key) => {
            if (isEmptyValue(row[key]))
                return null;
            if (key === 'links') {
                return (React.createElement(LinkRows, { key: key, value: row[key], onSelect: () => setSelectedField(key) }));
            }
            return (React.createElement(RoutingRow, { key: key, label: key, displayLabel: labelOverrides === null || labelOverrides === void 0 ? void 0 : labelOverrides[key], value: row[key], onSelect: setSelectedField }));
        })),
        selectedField ? (React.createElement(FieldDetail, { field: selectedField, value: row[selectedField], onClose: () => setSelectedField(null) })) : null));
}
const CORE_ROUTING_KEYS = new Set(['domain', 'action', 'route_node']);
export function RoutingIntentDebugPanel({ snapshot }) {
    const routing = routingView(snapshot);
    const visibleKeys = ROUTING_KEYS.filter((key) => CORE_ROUTING_KEYS.has(key) || !isEmptyValue(routing[key]));
    const hasAny = visibleKeys.some((key) => !isEmptyValue(routing[key]));
    if (!hasAny) {
        return (React.createElement("div", { className: "lg-routing-bubble" },
            React.createElement("p", { className: "lg-debug-empty" }, "No intent route for this turn.")));
    }
    return (React.createElement("div", { className: "lg-routing-bubble" },
        React.createElement(DebugFieldList, { keys: visibleKeys, row: routing, labelOverrides: {
                llm_used: 'router LLM',
                llm_role: 'router purpose',
                model_id: 'router model',
                prompt_sent: 'router prompt',
            } })));
}
/** @deprecated use RoutingIntentDebugPanel */
export const MexaStateDebugPanel = RoutingIntentDebugPanel;
const UNIPROT_DEBUG_KEYS = [
    'pdb_id',
    'pdb_status',
    'pdb_source',
    'uniprot_id',
    'links',
    'llm_context_used_for_questions',
    'storage',
    'source',
    'retrieved_fields',
    'normalized_target_fiche',
    'error',
];
const INTELLIGENCE_KEYS = [
    'technique',
    'llm_used',
    'model_id',
    'data_sources',
    'rules',
    'source_node',
    'bubble_source',
    'llm_role',
    'prompt_source',
    'prompt_sent',
    'data_used',
    'knowledge_entries',
    'knowledge_location',
    'knowledge_catalog_version',
];
export function LLMIntelligenceDebugPanel({ snapshot, }) {
    const intelligence = snapshot === null || snapshot === void 0 ? void 0 : snapshot.response_intelligence;
    if (!intelligence || typeof intelligence !== 'object') {
        return (React.createElement("div", { className: "lg-routing-bubble" },
            React.createElement("p", { className: "lg-debug-empty" }, "No response provenance for this turn.")));
    }
    const row = intelligence;
    return (React.createElement("div", { className: "lg-routing-bubble" },
        React.createElement(DebugFieldList, { keys: INTELLIGENCE_KEYS, row: row, labelOverrides: {
                llm_used: 'LLM used',
                llm_role: 'LLM purpose',
                model_id: 'model',
                prompt_sent: 'prompt sent',
                knowledge_entries: 'knowledge sources',
                data_used: 'session facts',
                prompt_source: 'prompt source',
                bubble_source: 'text source',
            } })));
}
export function UniProtDebugPanel({ snapshot, }) {
    var _a, _b;
    const debug = (_a = snapshot === null || snapshot === void 0 ? void 0 : snapshot.uniprot_debug) !== null && _a !== void 0 ? _a : (_b = snapshot === null || snapshot === void 0 ? void 0 : snapshot.target_context) === null || _b === void 0 ? void 0 : _b.uniprot_debug;
    if (!debug || typeof debug !== 'object') {
        return (React.createElement("div", { className: "lg-routing-bubble" },
            React.createElement("p", { className: "lg-debug-empty" }, "No UniProt data available for this session.")));
    }
    const row = debug;
    return (React.createElement("div", { className: "lg-routing-bubble" },
        React.createElement(DebugFieldList, { keys: UNIPROT_DEBUG_KEYS, row: row })));
}
